import type { ViewerShell } from '../../../shell';
import type { AssistantApi } from '../api';
import type { MessageHandlers } from '../messages';
import { askErrorText, type AssistantPanel } from '../panel';
import { button, el, resultCard } from '../tools/dom';
import { isToolDisabled, toolErrorText } from './logic';
import type { ModelTrace, ToolKey } from './shapes';
import { markToolOff } from './switches';

/** What every radiologist tool gets from the registration in ./index.ts. */
export interface RadiologistDeps {
  shell: ViewerShell;
  panel: AssistantPanel;
  api: AssistantApi;
  handlers: MessageHandlers;
}

export { button, el };

export const COMMON_TEXT = {
  testProvider: 'Proveedor de prueba, resultado no clínico',
  model: 'Modelo',
  version: 'versión',
  rulesOnly: 'Sin modelo: solo revisiones automáticas.',
} as const;

/**
 * What a failed tool call tells the reader: the round-3 refusals by name (409 tool_disabled also
 * switches the tool off for the rest of the page), else the Asistente's usual wording.
 */
export function toolFailure(error: unknown, key: ToolKey, fallback: string): string {
  if (isToolDisabled(error)) markToolOff(key);
  return toolErrorText(error) ?? (askErrorText(error) || fallback);
}

/** "Modelo: x · versión y" and the test-provider label, for a model-written answer. */
export function traceNodes(trace: ModelTrace): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (trace.isTest) out.push(el('div', 'assistant-test-label', COMMON_TEXT.testProvider));
  if (trace.model) out.push(el('div', 'assistant-trace', `${COMMON_TEXT.model}: ${trace.model}${trace.modelVersion ? ` · ${COMMON_TEXT.version} ${trace.modelVersion}` : ''}`));
  return out;
}

const cards = new Map<string, HTMLElement>();

/**
 * One card per tool in the conversation: a second run replaces the first (like "Lesiones"), and the
 * card belongs to the study it was asked for even if the reader moves on before the answer.
 */
export function toolCard(deps: RadiologistDeps, kind: string, title: string, study: string): { card: HTMLElement; body: HTMLElement } {
  const out = resultCard(kind, title);
  out.card.classList.add('assistant-radiologist');
  const key = `${kind}:${study}`;
  const previous = cards.get(key);
  if (previous) deps.panel.removeResult(previous);
  cards.set(key, out.card);
  deps.panel.appendResult(out.card, study);
  return out;
}

/** A "pensando…" line the tool replaces with its answer. */
export function loading(body: HTMLElement, text: string): void {
  const line = el('div', 'assistant-thinking', text);
  line.setAttribute('role', 'status');
  body.replaceChildren(line);
}

/** A labelled read-only block of model-written text (pre-wrapped, textContent only). */
export function textBlock(title: string, text: string, className = ''): HTMLElement {
  const box = el('div', `assistant-report-preview ${className}`.trim());
  box.append(el('div', 'assistant-report-preview-title', title), el('div', 'assistant-report-preview-text', text));
  return box;
}

/** Runs `work` with `trigger` disabled and relabelled, giving its label back afterwards. */
export async function busy<T>(trigger: HTMLButtonElement, label: string, work: () => Promise<T>): Promise<T> {
  const idle = trigger.textContent ?? '';
  trigger.disabled = true;
  trigger.textContent = label;
  try {
    return await work();
  } finally {
    trigger.disabled = false;
    trigger.textContent = idle;
  }
}

/** Calls `stop` when the Asistente tab (or the drawer holding it) is hidden. */
export function onPanelHidden(panel: AssistantPanel, stop: () => void): void {
  const host = panel.root.parentElement ?? panel.root;
  new MutationObserver(() => {
    if (host.hidden || panel.root.hidden) stop();
  }).observe(host, { attributes: true, attributeFilter: ['hidden'] });
}
